/* ============================================================================
   Peso y balance.

   Devuelve siempre el desglose completo (cada ítem con su brazo y su momento),
   no solo el veredicto. Un piloto que ve "FUERA DE ENVOLVENTE" necesita saber
   qué mover, y para eso hace falta ver de dónde sale cada kilo-metro.
   ========================================================================== */

/**
 * Límite de CG interpolado para un peso dado, sobre una envolvente definida
 * como lista de puntos [peso_kg, cg_m].
 *
 * Fuera del rango de pesos publicado se devuelve el extremo, no una
 * extrapolación: la envolvente no continúa más allá de donde la imprimieron.
 */
export function limiteEnPeso(puntos, pesoKg) {
  if (!Array.isArray(puntos) || puntos.length === 0) return null;
  if (!Number.isFinite(pesoKg)) return null;

  if (pesoKg <= puntos[0][0]) return puntos[0][1];
  const ultimo = puntos[puntos.length - 1];
  if (pesoKg >= ultimo[0]) return ultimo[1];

  for (let i = 0; i < puntos.length - 1; i++) {
    const [w0, c0] = puntos[i];
    const [w1, c1] = puntos[i + 1];
    if (pesoKg >= w0 && pesoKg <= w1) {
      if (w1 === w0) return c0;
      return c0 + (c1 - c0) * ((pesoKg - w0) / (w1 - w0));
    }
  }
  return ultimo[1];
}

/**
 * Calcula peso y balance de una configuración de carga.
 *
 * @param {object} perfil perfil de aeronave (brazos, envolvente, límites)
 * @param {object} carga
 * @param {number} carga.pilotoKg
 * @param {number} carga.paxDelanteKg
 * @param {number} carga.paxAtrasKg
 * @param {Array<{key:string, kg:number}>} carga.compartimentos
 * @param {number} carga.cargaExternaKg
 * @param {number} carga.combustibleLitros
 * @returns {object} desglose, totales y veredicto
 */
export function calcularPesoBalance(perfil, carga = {}) {
  if (!perfil) {
    return {
      items: [],
      pesoTotalKg: 0,
      momentoTotal: 0,
      cg: null,
      dentroDeEnvolvente: null,
      motivo: "sin perfil de aeronave",
    };
  }

  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const combustibleKg = num(carga.combustibleLitros) * num(perfil.fuelDensity);
  const cargaExternaKg = num(carga.cargaExternaKg);

  const compartimentos = (perfil.cargoCompartments || []).map((c) => {
    const declarado = (carga.compartimentos || []).find((x) => x.key === c.key);
    const kg = num(declarado?.kg);
    return {
      ...c,
      kg,
      excedido: c.maxKg > 0 && kg > c.maxKg,
    };
  });

  // El peso vacío es de la matrícula, no del tipo: sale de un pesaje con fecha.
  // Si la aeronave trae el suyo, manda sobre el valor genérico del perfil. Lo
  // mismo con el brazo, que sale del acta de pesaje. Cuando no hay pesaje se
  // cae al valor del tipo, y quien llama debe decir en pantalla que es
  // estimado — acá no se puede distinguir un número pesado de uno heredado.
  const pesoVacioKg = Number.isFinite(Number(carga.pesoVacioKg))
    ? Number(carga.pesoVacioKg)
    : num(perfil.emptyKg);
  const brazoVacioM = Number.isFinite(Number(carga.brazoVacioM))
    ? Number(carga.brazoVacioM)
    : num(perfil.emptyArm);

  const items = [
    { etiqueta: "Vacío", kg: pesoVacioKg, brazo: brazoVacioM },
    { etiqueta: "Piloto", kg: num(carga.pilotoKg), brazo: num(perfil.arms?.pilot) },
    { etiqueta: "Pax delante", kg: num(carga.paxDelanteKg), brazo: num(perfil.arms?.paxFront) },
    { etiqueta: "Pax atrás", kg: num(carga.paxAtrasKg), brazo: num(perfil.arms?.paxRear) },
    ...compartimentos.map((c) => ({
      etiqueta: `Equipaje ${c.label.toLowerCase()}`,
      kg: c.kg,
      brazo: num(perfil.arms?.[c.armKey]),
    })),
    { etiqueta: "Carga externa", kg: cargaExternaKg, brazo: num(perfil.arms?.external) },
    { etiqueta: "Combustible", kg: combustibleKg, brazo: num(perfil.arms?.fuel) },
  ].map((it) => ({ ...it, momento: it.kg * it.brazo }));

  const pesoTotalKg = items.reduce((s, it) => s + it.kg, 0);
  const momentoTotal = items.reduce((s, it) => s + it.momento, 0);
  const cg = pesoTotalKg > 0 ? momentoTotal / pesoTotalKg : null;

  // Con carga externa liberable la envolvente y el peso máximo son otros.
  const conCargaExterna = cargaExternaKg > 0;
  const pesoMaximoKg = conCargaExterna ? num(perfil.maxExternalKg) : num(perfil.maxInternalKg);
  const puntosTraseros = conCargaExterna
    ? perfil.envelope?.rearwardExternal
    : perfil.envelope?.rearwardInternal;

  const limiteDelantero = limiteEnPeso(perfil.envelope?.forward, pesoTotalKg);
  const limiteTrasero = limiteEnPeso(puntosTraseros, pesoTotalKg);

  const excedeMTOW = pesoMaximoKg > 0 && pesoTotalKg > pesoMaximoKg;
  const compartimentoExcedido = compartimentos.find((c) => c.excedido) || null;

  let cgDentro = null;
  if (cg !== null && limiteDelantero !== null && limiteTrasero !== null) {
    const min = Math.min(limiteDelantero, limiteTrasero);
    const max = Math.max(limiteDelantero, limiteTrasero);
    cgDentro = cg >= min && cg <= max;
  }

  const motivos = [];
  if (excedeMTOW) {
    motivos.push(
      `Excede el peso máximo (${Math.round(pesoTotalKg)} kg contra ${pesoMaximoKg} kg)`,
    );
  }
  if (cgDentro === false) {
    const lado = cg < Math.min(limiteDelantero, limiteTrasero) ? "adelante" : "atrás";
    motivos.push(`CG ${cg.toFixed(3)} m fuera de envolvente (corrido hacia ${lado})`);
  }
  if (compartimentoExcedido) {
    motivos.push(
      `Compartimento ${compartimentoExcedido.label.toLowerCase()} excedido ` +
        `(${compartimentoExcedido.kg} kg contra ${compartimentoExcedido.maxKg} kg)`,
    );
  }

  return {
    items,
    compartimentos,
    pesoTotalKg,
    momentoTotal,
    cg,
    pesoMaximoKg,
    conCargaExterna,
    limiteDelantero,
    limiteTrasero,
    cgDentro,
    excedeMTOW,
    dentroDeEnvolvente: cgDentro === null ? null : cgDentro && !excedeMTOW && !compartimentoExcedido,
    motivo: motivos.length ? motivos.join(". ") : null,
  };
}

/**
 * Trazo cerrado de la envolvente, para dibujarla.
 * Sube por el límite delantero, corta en el peso máximo, baja por el trasero
 * y cierra.
 */
export function trazoEnvolvente(perfil, { conCargaExterna = false } = {}) {
  const env = perfil?.envelope;
  if (!env) return [];
  const pesoMax = conCargaExterna ? perfil.maxExternalKg : perfil.maxInternalKg;
  const traseros = conCargaExterna ? env.rearwardExternal : env.rearwardInternal;

  const delante = (env.forward || []).filter((p) => p[0] <= pesoMax);
  const atras = [...(traseros || [])].filter((p) => p[0] <= pesoMax).reverse();
  if (delante.length === 0) return [];

  return [
    ...delante.map(([w, c]) => ({ cg: c, peso: w })),
    ...atras.map(([w, c]) => ({ cg: c, peso: w })),
    { cg: delante[0][1], peso: delante[0][0] },
  ];
}

/**
 * Peso y centro de gravedad AL ATERRIZAJE.
 *
 * El peso y balance de despegue no dice si el aterrizaje es legal. Quemar
 * combustible no solo aliviana: MUEVE el CG, porque el tanque tiene su propio
 * brazo. Si el tanque está por detrás del CG de despegue, gastarlo corre el
 * centro hacia adelante; si está por delante, hacia atrás. Un helicóptero
 * puede despegar dentro de envolvente y llegar fuera.
 *
 * Y la envolvente no es un rectángulo: sus límites dependen del peso. Al
 * aterrizar se pesa menos, así que hay que volver a leer los límites A ESE
 * PESO, no a los del despegue.
 *
 * Solo se descuenta el combustible de la ETAPA, no la reserva: la reserva
 * llega al destino dentro del tanque y sigue pesando.
 *
 * @param {object} perfil perfil de la aeronave
 * @param {object} despegue resultado de calcularPesoBalance al despegue
 * @param {number} consumidoKg combustible de la etapa, en kilos
 * @param {number} brazoCombustibleM brazo del tanque
 */
export function estadoEnAterrizaje(perfil, despegue, consumidoKg, brazoCombustibleM) {
  const invalido = (motivo) => ({
    valido: false, motivo,
    pesoTotalKg: null, cg: null, cgDentro: null, dentroDeEnvolvente: null,
    combustibleConsumidoKg: null, combustibleRestanteKg: null,
    desplazamientoCgM: null, limiteDelantero: null, limiteTrasero: null,
  });

  if (!despegue || !Number.isFinite(despegue.pesoTotalKg) || !Number.isFinite(despegue.cg)) {
    return invalido("Sin peso y balance de despegue no se puede calcular el de aterrizaje.");
  }
  if (!Number.isFinite(consumidoKg)) {
    return invalido("Sin consumo de la etapa no se puede calcular el aterrizaje. Falta la ruta o la carta de combustible.");
  }
  if (consumidoKg < 0) return invalido("El consumo de la etapa no puede ser negativo.");
  if (!Number.isFinite(brazoCombustibleM)) {
    return invalido("Sin el brazo del tanque no se puede saber cómo mueve el CG el combustible gastado.");
  }

  // El dominio nombra las partidas con `etiqueta` y el legado con `label`.
  // Buscar solo por uno devolvía null en silencio, y con null no se puede
  // comprobar que el combustible de la etapa quepa en el que hay a bordo.
  const nombreDe = (i) => i?.etiqueta ?? i?.label ?? "";
  const combustibleEmbarcado = despegue.items?.find((i) => /combustible/i.test(nombreDe(i)));
  const embarcadoKg = Number.isFinite(combustibleEmbarcado?.kg) ? combustibleEmbarcado.kg : null;
  if (embarcadoKg !== null && consumidoKg > embarcadoKg + 1e-9) {
    return invalido(
      `La etapa consume ${Math.round(consumidoKg)} kg y a bordo hay ${Math.round(embarcadoKg)} kg: ` +
      "no se llega al destino. Revisa el combustible o la ruta.",
    );
  }

  const pesoTotalKg = despegue.pesoTotalKg - consumidoKg;
  const momentoTotal = despegue.momentoTotal - consumidoKg * brazoCombustibleM;
  if (pesoTotalKg <= 0) return invalido("El peso al aterrizaje no puede ser cero o negativo.");

  const cg = momentoTotal / pesoTotalKg;

  // La envolvente se vuelve a leer AL PESO DE ATERRIZAJE.
  const conCargaExterna = Boolean(despegue.conCargaExterna);
  const puntosTraseros = conCargaExterna
    ? perfil?.envelope?.rearwardExternal
    : perfil?.envelope?.rearwardInternal;
  const limiteDelantero = limiteEnPeso(perfil?.envelope?.forward, pesoTotalKg);
  const limiteTrasero = limiteEnPeso(puntosTraseros, pesoTotalKg);

  let cgDentro = null;
  if (limiteDelantero !== null && limiteTrasero !== null) {
    const min = Math.min(limiteDelantero, limiteTrasero);
    const max = Math.max(limiteDelantero, limiteTrasero);
    cgDentro = cg >= min && cg <= max;
  }

  const desplazamientoCgM = cg - despegue.cg;
  const motivos = [];
  if (cgDentro === false) {
    const lado = cg < Math.min(limiteDelantero, limiteTrasero) ? "adelante" : "atrás";
    motivos.push(
      `Al aterrizar el CG queda en ${cg.toFixed(3)} m, fuera de envolvente (corrido hacia ${lado}). ` +
      "Despegar dentro no alcanza: hay que llegar dentro.",
    );
  }
  if (cgDentro === null) {
    motivos.push("La envolvente no cubre el peso de aterrizaje: no se puede afirmar que el CG esté dentro.");
  }

  return {
    valido: true,
    pesoTotalKg: Math.round(pesoTotalKg * 10) / 10,
    momentoTotal,
    cg: Math.round(cg * 10000) / 10000,
    combustibleConsumidoKg: Math.round(consumidoKg * 10) / 10,
    combustibleRestanteKg: embarcadoKg === null ? null : Math.round((embarcadoKg - consumidoKg) * 10) / 10,
    desplazamientoCgM: Math.round(desplazamientoCgM * 10000) / 10000,
    // Hacia dónde se corrió: es lo que el piloto necesita saber para decidir
    // si mover carga antes de salir.
    sentido: Math.abs(desplazamientoCgM) < 0.0005 ? "sin cambio"
      : desplazamientoCgM > 0 ? "hacia atrás" : "hacia adelante",
    limiteDelantero,
    limiteTrasero,
    cgDentro,
    dentroDeEnvolvente: cgDentro,
    motivo: motivos.length ? motivos.join(" ") : null,
  };
}
